<template>
  <NuxtLink
    class="card"
    :to="`/${locale}${blok.full_slug}`"
  >
    <div class="card__content">
      <h2>{{ blok.title }}</h2>
      <p
        v-dompurify-html="renderRichText(blok.cardDescription)"
        class="body-1"
      />
      <div class="summary">
        <span class="body-5">{{ $t(`vacancy.department.${blok.department}`) }}</span>
        <span class="body-5">
          {{ $t(`vacancy.summary.location.${blok.location}`) }}
        </span>
        <span class="body-5">
          {{ activeDate }}
        </span>
        <span class="body-5 position-count">
          {{ blok.openPositions }}
        </span>
      </div>
    </div>
    <div
      v-if="blok.showImage"
      class="card__image"
    >
      <NuxtImg
        provider="storyblok"
        :src="blok.heroImage?.filename"
        :alt="blok.heroImage?.alt"
        :width="400"
        loading="lazy"
        format="webp"
      />
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
  import type IVacancyCard from '~/types/Storyblok/Vacancy/VacancyCard'
  import formatUtil from '~/utils/formatUtil'
  import renderRichText from '~/utils/renderSbRichText'

  const { locale } = useI18n()

  const {
    blok: { internalDate }
  } = defineProps<{
    blok: IVacancyCard
  }>()

  const activeDate = formatUtil(new Date(internalDate.replace(' ', 'T')), 'dd.MM.yyyy', locale.value)
</script>

<style lang="scss" scoped>
  @import '~/assets/styles/common.scss';

  .card {
    background-color: $bg-grey;
    border-radius: 8px;
    cursor: pointer;
    display: flex;

    @include tablet-up {
      background-color: $card-bg;
      box-shadow: 0px 4px 4px rgba(106, 119, 142, 0.15);
      border-radius: 0;
      flex-direction: row;
      justify-content: space-between;
    }

    &__content {
      flex: 1;
      padding: rem(40) rem(16);
      text-align: center;

      @include tablet-up {
        padding: rem(16) rem(32);
        text-align: left;
      }

      h2 {
        margin: 0 0 rem(16);
        font-size: rem(24);
        line-height: 40px;
        font-weight: 500;
      }

      .summary {
        span {
          &:not(:last-child) {
            border-right: 2px solid $bt-blue;
            padding-right: rem(4);
          }

          &:not(:first-child) {
            padding-left: rem(4);
          }

          &.position-count {
            &::before {
              display: inline-block;
              content: '';
              background-image: url('~/assets/icons/people.svg');
              background-size: cover;
              margin-right: rem(4);
              height: 14px;
              width: 14px;
            }
          }
        }
      }
    }

    &__image {
      display: none;
      width: 235px;
      min-height: 100%;
      overflow: hidden;

      @include tablet-up {
        display: block;
      }

      img {
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
    }
  }
</style>
